import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, Pencil, Trash2, Star } from 'lucide-react';
import { useAuth, type ShippingAddress } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import toast, { Toaster } from 'react-hot-toast';
import { getAuthApiUrl } from '../lib/apiConfig';

const emptyForm = {
  first_name: '',
  last_name: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  district: '',
  state: '',
  pincode: '',
};

export default function Addresses() {
  const { token, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCheckout = Boolean((location.state as { fromCheckout?: boolean })?.fromCheckout);

  const [list, setList] = useState<ShippingAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = useCallback(() => {
    const t = token ?? localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Token ${t}` } : {}),
    };
  }, [token]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getAuthApiUrl('/api/shipping-addresses/'), {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setList(Array.isArray(data) ? data : (data.results ?? []));
      } else {
        toast.error('Could not load addresses');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (a: ShippingAddress) => {
    setEditingId(a.id);
    setForm({
      first_name: a.first_name,
      last_name: a.last_name || '',
      phone: a.phone,
      address_line1: a.address_line1,
      address_line2: a.address_line2 || '',
      district: a.district,
      state: a.state,
      pincode: a.pincode,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url =
        editingId != null
          ? getAuthApiUrl(`/api/shipping-addresses/${editingId}/`)
          : getAuthApiUrl('/api/shipping-addresses/');
      const method = editingId != null ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          last_name: form.last_name || '',
          address_line2: form.address_line2 || '',
        }),
      });
      if (res.ok) {
        toast.success(editingId != null ? 'Address updated' : 'Address saved');
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
        await loadList();
        await refreshUserProfile();
      } else {
        const err = await res.json().catch(() => ({}));
        const msg =
          err.detail || err.message || (err.pincode && err.pincode[0]) || 'Save failed';
        toast.error(typeof msg === 'string' ? msg : 'Save failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      const res = await fetch(getAuthApiUrl(`/api/shipping-addresses/${id}/`), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 204 || res.ok) {
        toast.success('Address removed');
        await loadList();
        await refreshUserProfile();
      } else {
        toast.error('Could not delete');
      }
    } catch {
      toast.error('Network error');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      const res = await fetch(getAuthApiUrl(`/api/shipping-addresses/${id}/set-default/`), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success('Default address updated');
        await loadList();
        await refreshUserProfile();
      } else {
        toast.error('Could not update default');
      }
    } catch {
      toast.error('Network error');
    }
  };

  const goBack = () => {
    if (fromCheckout) {
      navigate('/discover', { replace: true, state: { reopenCart: true } });
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <Toaster position="top-center" />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={goBack} className="hover:bg-gray-800/50">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gradient flex items-center gap-2">
              <MapPin className="w-7 h-7" />
              Delivery addresses
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {fromCheckout ? 'Add an address, then return to checkout.' : 'Manage where we ship your orders.'}
            </p>
          </div>
        </div>

        {!showForm && (
          <Button onClick={openNew} className="mb-6 gap-2 w-full sm:w-auto">
            <Plus size={18} />
            Add address
          </Button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="cosmic-card p-6 space-y-4 mb-8">
            <h2 className="text-lg font-semibold text-white">
              {editingId != null ? 'Edit address' : 'New address'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fn">First name</Label>
                <Input
                  id="fn"
                  required
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ln">Last name</Label>
                <Input
                  id="ln"
                  value={form.last_name}
                  onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ph">Mobile number</Label>
              <Input
                id="ph"
                type="tel"
                required
                placeholder="+91 …"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="l1">Address line 1</Label>
              <Input
                id="l1"
                required
                value={form.address_line1}
                onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="l2">Address line 2 (optional)</Label>
              <Input
                id="l2"
                value={form.address_line2}
                onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dist">District</Label>
                <Input
                  id="dist"
                  required
                  value={form.district}
                  onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="st">State</Label>
                <Input
                  id="st"
                  required
                  value={form.state}
                  onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="pin">PIN code</Label>
                <Input
                  id="pin"
                  required
                  maxLength={6}
                  inputMode="numeric"
                  value={form.pincode}
                  onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save address'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-muted-foreground text-center py-12">Loading…</p>
        ) : list.length === 0 && !showForm ? (
          <div className="cosmic-card p-8 text-center text-muted-foreground">
            <p>No saved addresses yet.</p>
            <Button onClick={openNew} className="mt-4 gap-2">
              <Plus size={18} />
              Add your first address
            </Button>
          </div>
        ) : (
          <ul className="space-y-3">
            {list.map((a) => (
              <li key={a.id} className="cosmic-card p-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <p className="font-semibold text-white">
                    {[a.first_name, a.last_name].filter(Boolean).join(' ')}
                    {a.is_default ? (
                      <span className="ml-2 inline-block text-xs font-normal text-primary">
                        (Default)
                      </span>
                    ) : null}
                  </p>
                  <p className="text-sm text-gray-400">{a.phone}</p>
                  <p className="text-sm text-gray-300 mt-2">
                    {a.address_line1}
                    {a.address_line2 ? `, ${a.address_line2}` : ''}
                  </p>
                  <p className="text-sm text-gray-300">
                    {a.district}, {a.state} — {a.pincode}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!a.is_default && (
                    <Button type="button" variant="secondary" size="sm" className="gap-1" onClick={() => handleSetDefault(a.id)}>
                      <Star className="w-4 h-4" />
                      Set default
                    </Button>
                  )}
                  <Button type="button" variant="secondary" size="sm" className="gap-1" onClick={() => openEdit(a)}>
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1 text-red-400"
                    onClick={() => handleDelete(a.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
